import type { LinkedTenant } from '@shared/types/auth';
import { getNotificationTarget } from './notificationTargets';
import type { Notification } from './api';
import type { PendingTenantSwitch } from './tenantSwitchPromptStore';

export type RoutableNotification = Pick<
  Notification,
  'action_url' | 'notification_type' | 'related_id' | 'related_entity_type' | 'metadata' | 'tenant_id'
>;

export interface RouteNotificationDeps {
  activeTenantId: string | null;
  linkedTenants: LinkedTenant[];
  /** Navigate within the current tenant (SPA navigate or global navigateTo). */
  navigate: (path: string) => void;
  /** Open the confirm-and-switch prompt for a notification belonging to another linked tenant. */
  requestSwitch: (req: PendingTenantSwitch) => void;
  /** Open an external URL. */
  openExternal: (url: string) => void;
}

/**
 * Resolve a notification (push or in-app) to its destination and act on it:
 *  - external link → open it
 *  - same tenant   → navigate
 *  - other linked tenant → prompt to switch, then route after switching
 *  - other unlinked tenant → best-effort navigate in the current tenant
 */
export function routeNotification(n: RoutableNotification, deps: RouteNotificationDeps): void {
  const target = getNotificationTarget(n);
  if (!target) return;

  if (target.kind === 'external') {
    deps.openExternal(target.url);
    return;
  }

  const targetTenantId = n.tenant_id?.trim();
  if (targetTenantId && deps.activeTenantId && targetTenantId !== deps.activeTenantId) {
    const linked = deps.linkedTenants.find((t) => t.tenant_id === targetTenantId);
    if (linked) {
      deps.requestSwitch({ tenantId: targetTenantId, tenantName: linked.name, path: target.path });
      return;
    }
    // Not a tenant this user can access — fall through to a best-effort navigate.
  }

  deps.navigate(target.path);
}
